import { GTMCategories, GTMLists, GTMNames, GTMObjectEvents, GtmDataLayer, GtmDimensions, GtmEventNames } from 'google-tag-manager';
import { DateTime } from 'luxon';
import { FLIGHT_SEQUENCE_OUTBOUND } from 'server-lib/apis/commercial/methods/search-v2/constants';
import { getNumberOfSeatsLeft } from 'server-lib/domain/utils/flightDetailUtils';
import { GTMSharedContext, GTMSharedContextStore } from './context';
import {
  getComponentSenderFromCookieIfAvailable,
  getDaysToDeparture,
  getFlightsRoute,
  getReturnDateFromSearchCookie,
  groupImpressionsByProperty,
  itemPriceDifference,
  pushToDataLayer
} from './utils/helpers';
import { FunnelQueryCookieObject, parseFunnelQuery } from '../cookie-utils';
import { searchDataStore } from '../stores';

export type SearchCriteriaBrowseLocation =
  | '3 week view'
  | 'recent search'
  | 'left arrow outbound'
  | 'right arrow outbound'
  | 'left arrow inbound'
  | 'right arrow inbound'
  | 'change currency'
  | 'confirm passengers'
  | 'refetch flights';

type Products = GtmDataLayer;
type Impressions = GtmDataLayer;

export type SearchCriteriaAnalytics = {
  [GtmDimensions.event]: GtmEventNames.searchCriteriaBrowse | GtmEventNames.searchCriteria;
  location?: SearchCriteriaBrowseLocation;
  ecommerce: {
    detail: {
      products: Products[];
    };
    impressions: Impressions[];
  };
};

export const pushSearchAnalyticsData = (data: FunnelQueryCookieObject, browseLocation?: SearchCriteriaBrowseLocation) => {
  // get reusable data from shared context
  const sharedContext = GTMSharedContextStore.getState();
  const dataLayer: SearchCriteriaAnalytics = {
    ecommerce: {
      detail: {
        products: [getFlightProducts(sharedContext, data, !!browseLocation)]
      },
      impressions: getFlightGridImpressions(sharedContext, data, !!browseLocation)
    },
    [GtmDimensions.event]: browseLocation ? GtmEventNames.searchCriteriaBrowse : GtmEventNames.searchCriteria
  };

  if (browseLocation) dataLayer.location = browseLocation;

  pushToDataLayer(dataLayer);
};

const getFlightProducts = (
  sharedContext: GTMSharedContext,
  flightQuery: FunnelQueryCookieObject,
  isBrowse: boolean = false
): Products => {
  const journeyType = parseFunnelQuery()?.returnDate ? 'Return' : 'One Way';
  const componentSender = getComponentSenderFromCookieIfAvailable(flightQuery);
  const fareType = sharedContext.basket?.journeyPairs?.outboundJourney?.airfare.airfares[0].fareType;
  const searchStore = searchDataStore.getState();
  const defaultCurrency = searchStore.metaData.currencyCode;
  const paxConfig = `A: ${flightQuery.numberOfAdults}, C: ${flightQuery.numberOfChildren}, I: ${flightQuery.numberOfInfants}`;

  const isMarketGroupSearch = searchStore.journeyPairs.length > 1;

  let outboundLastBooked = '';
  let outboundCurrentlyLooking = '';
  let inboundLastBooked = '';
  let inboundCurrentlyLooking = '';
  let urgencyMessageContent = '';
  let urgencyMessageId = '';
  let hasLookAndBook = false;

  if (!isMarketGroupSearch) {
    const outboundJourney = searchStore.journeyPairs[0]?.outbound;
    const inboundJourney = searchStore.journeyPairs[0]?.inbound;
    if ((outboundJourney && outboundJourney.analytics) || (inboundJourney && inboundJourney.analytics)) {
      hasLookAndBook = true;
      outboundLastBooked = outboundJourney.analytics?.lookAndBook?.LastBooked?.MinutesAgo
        ? outboundJourney.analytics.lookAndBook?.LastBooked.MinutesAgo >= 60
          ? `${Math.round(outboundJourney.analytics.lookAndBook?.LastBooked.MinutesAgo / 60)} hours`
          : `${outboundJourney.analytics.lookAndBook?.LastBooked.MinutesAgo} minutes`
        : 'NULL';
      outboundCurrentlyLooking = outboundJourney.analytics?.lookAndBook?.CurrentlyLooking?.Count
        ? `${outboundJourney.analytics.lookAndBook?.CurrentlyLooking.Count} people`
        : 'NULL';
      inboundLastBooked = inboundJourney?.analytics?.lookAndBook?.LastBooked?.MinutesAgo
        ? inboundJourney.analytics.lookAndBook?.LastBooked.MinutesAgo >= 60
          ? `${Math.round(inboundJourney.analytics.lookAndBook?.LastBooked.MinutesAgo / 60)} hours`
          : `${inboundJourney.analytics.lookAndBook?.LastBooked.MinutesAgo} minutes`
        : 'NULL';
      inboundCurrentlyLooking = inboundJourney?.analytics?.lookAndBook?.CurrentlyLooking?.Count
        ? `${inboundJourney.analytics.lookAndBook?.CurrentlyLooking.Count} people`
        : 'NULL';
      urgencyMessageContent = `${outboundLastBooked}|${outboundCurrentlyLooking}|${inboundLastBooked}|${inboundCurrentlyLooking}`;
      urgencyMessageId = `${outboundLastBooked !== 'NULL' ? 'LastBooked' : 'NULL'}|${outboundCurrentlyLooking !== 'NULL' ? 'CurrentlyLooking' : 'NULL'}|${inboundLastBooked !== 'NULL' ? 'LastBooked' : 'NULL'}|${inboundCurrentlyLooking !== 'NULL' ? 'CurrentlyLooking' : 'NULL'}`;
    }
  }

  return {
    [GtmDimensions.category]: isBrowse ? GTMCategories.searchCriteriaBrowse : GTMCategories.searchCriteria,
    [GtmDimensions.name]: GTMNames.flights,
    [GtmDimensions.id]:
      journeyType === 'One Way'
        ? `${flightQuery.originIata}${flightQuery.destinationIata}`
        : `${flightQuery.originIata}${flightQuery.destinationIata}|${flightQuery.destinationIata}${flightQuery.originIata}`,
    [GtmDimensions.position]: '', // no data
    [GtmDimensions.quantity]: flightQuery.numberOfAdults + flightQuery.numberOfChildren,
    [GtmDimensions.price]: '', // no data
    [GtmDimensions.variant]: '',
    [GtmDimensions.currencyCode]: sharedContext.basket?.isoCurrencyCode || defaultCurrency, // TODO CDT-560 change logic for the fallback currency
    [GtmDimensions.timestamp]: DateTime.now().toFormat('y-MM-dd_HH:mm:ss'),
    [GtmDimensions.basketId]: '', // no data
    [GtmDimensions.basketRevenue]: sharedContext.basket?.aggregate?.totals.total.raw || 0,
    [GtmDimensions.departureAirportCode]: flightQuery.originIata,
    [GtmDimensions.departureAirportCountryCode]: sharedContext.getAirportCountryCode(flightQuery.originIata) || '',
    [GtmDimensions.destinationAirportCode]: flightQuery.destinationIata,
    [GtmDimensions.destinationAirportCountryCode]: sharedContext.getAirportCountryCode(flightQuery.destinationIata) || '',
    [GtmDimensions.searchedMultipleDepartureAirports]: sharedContext.isSearchedMultipleAirports(flightQuery.originIata),
    [GtmDimensions.searchedNoMultipleDepartureAirports]: sharedContext.noOfIatasForMarketGroup(flightQuery.originIata),
    [GtmDimensions.searchedMultipleDestinationAirports]: sharedContext.isSearchedMultipleAirports(flightQuery.destinationIata),
    [GtmDimensions.searchedNoMultipleDestinationAirports]: sharedContext.noOfIatasForMarketGroup(flightQuery.destinationIata),
    [GtmDimensions.journeyType]: journeyType,
    [GtmDimensions.departureDate]: flightQuery.outboundDate,
    [GtmDimensions.daysToDeparture]: getDaysToDeparture(flightQuery.outboundDate),
    [GtmDimensions.tripDuration]: sharedContext.getTripDuration(
      flightQuery.isOneWay,
      DateTime.fromFormat(flightQuery.outboundDate, 'y-MM-dd'),
      flightQuery.returnDate ? DateTime.fromFormat(flightQuery.returnDate, 'y-MM-dd') : undefined
    ),
    [GtmDimensions.paxCount]: flightQuery.numberOfAdults + flightQuery.numberOfChildren,
    [GtmDimensions.paxConfig]: paxConfig,
    [GtmDimensions.noOfAdults]: flightQuery.numberOfAdults,
    [GtmDimensions.noOfChildren]: flightQuery.numberOfChildren,
    [GtmDimensions.noOfInfants]: flightQuery.numberOfInfants,
    [GtmDimensions.flightRoute]: getFlightsRoute(flightQuery),
    [GtmDimensions.noOfFlightResults]: sharedContext.noOfFlights(),
    [GtmDimensions.minFlightPrice]: sharedContext.getMinPrices(),
    [GtmDimensions.maxFlightPrice]: sharedContext.getMaxPrices(),
    [GtmDimensions.resultsView]: '3 day view',
    [GtmDimensions.alternativeAirports]: '', // Need to discuss
    [GtmDimensions.noAlternativeAirports]: '', // Need to discuss
    [GtmDimensions.alternativeAirportSelected]: '', // Need to discuss
    [GtmDimensions.lowestFareSelected]: '', // no data
    [GtmDimensions.showFlexiFares]: 'No',
    [GtmDimensions.noOfFlexiFaresAvailable]: 0,
    [GtmDimensions.fareType]: fareType
      ? String(fareType).charAt(0).toUpperCase() + String(fareType).slice(1).toLowerCase()
      : 'Standard',
    [GtmDimensions.onsiteSearchOrigin]: componentSender || '',
    [GtmDimensions.objectEvent]: GTMObjectEvents.pageload,
    [GtmDimensions.list]: GTMLists.flightGrid,
    [GtmDimensions.returnDate]: getReturnDateFromSearchCookie(),
    [GtmDimensions.noOfSoldOutFlights]: sharedContext.getNoOfSoldOutFlights(),
    [GtmDimensions.urgencyMessageContent]: urgencyMessageContent,
    [GtmDimensions.urgencyMessageType]: hasLookAndBook ? 'Route' : 'NULL',
    [GtmDimensions.urgencyMessageId]: urgencyMessageId,
    [GtmDimensions.eJPlusPassengers]: sharedContext.getEJPlusPassengers()
  };
};

const getFlightGridImpressions = (
  sharedContext: GTMSharedContext,
  flightQuery: FunnelQueryCookieObject,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _isBrowse: boolean = false
): Impressions[] => {
  const deepLinkParams = new URLSearchParams(flightQuery.deepLinkParams);
  const journeyType = deepLinkParams.get('isOneWay') === 'on' ? 'One Way' : 'Return';
  const paxConfig = `A: ${flightQuery.numberOfAdults}, C: ${flightQuery.numberOfChildren}, I: ${flightQuery.numberOfInfants}`;
  const searchStore = searchDataStore.getState();
  const defaultCurrency = searchStore.metaData.currencyCode;
  let outboundIndex = 0;
  let inboundIndex = 0;

  const flights = sharedContext.getFlights().map(flight => {
    const isOutboundFlight = flight.flightSequence === FLIGHT_SEQUENCE_OUTBOUND;

    if (isOutboundFlight) {
      outboundIndex += 1;
    } else {
      inboundIndex += 1;
    }

    const numberOfSeatsLeft = getNumberOfSeatsLeft(flight);

    const showSeatsLeftAtMessage =
      sharedContext.flightGridProps && sharedContext.flightGridProps.maxSeatsAvailable >= numberOfSeatsLeft;

    const urgencyMessagePart1 = showSeatsLeftAtMessage
      ? `${numberOfSeatsLeft} ${numberOfSeatsLeft > 1 ? 'seats' : 'seat'} left at`
      : 'NULL';
    const urgencyMessagePart2 = flight?.fares?.ADT?.PROMOTIONAL !== undefined ? 'SALE' : 'NULL';
    const urgencyMessagePart3 = flight?.fares?.ADT?.PROMOTIONAL !== undefined ? 'Flight fare sale' : 'NULL';
    const urgencyMessageContent = `${urgencyMessagePart1}|${urgencyMessagePart2}|${urgencyMessagePart3}`;

    return {
      [GtmDimensions.category]: isOutboundFlight ? 'Flight: Outbound' : 'Flight: Inbound',
      [GtmDimensions.name]: `${flight.departureAirportCode}-${flight.arrivalAirportCode}`,
      [GtmDimensions.id]: `${flight.icaoCarrierCode}${flight.flightNumber}`,
      [GtmDimensions.position]: isOutboundFlight ? outboundIndex : inboundIndex,
      [GtmDimensions.quantity]: flightQuery.numberOfAdults + flightQuery.numberOfChildren,
      [GtmDimensions.price]: flight.soldOut
        ? 0
        : flight.fares?.ADT?.PROMOTIONAL?.unitPrice.grossPrice || flight.fares?.ADT?.STANDARD?.unitPrice.grossPrice,
      [GtmDimensions.was_price]: flight.fares?.ADT?.PROMOTIONAL?.unitPrice.grossPrice
        ? flight.fares?.ADT?.STANDARD?.unitPrice.grossPrice
        : null,
      [GtmDimensions.item_price_difference]: itemPriceDifference(
        flight?.fares?.ADT?.PROMOTIONAL?.unitPrice.grossPrice,
        flight?.fares?.ADT?.STANDARD?.unitPrice.grossPrice
      ),
      [GtmDimensions.variant]: '',
      [GtmDimensions.currencyCode]: sharedContext.basket?.isoCurrencyCode || defaultCurrency,
      [GtmDimensions.journeyType]: journeyType,
      [GtmDimensions.departureDate]: DateTime.fromISO(flight.localDepartureDateTime!).toFormat('y-MM-dd'),
      [GtmDimensions.flightTime]: DateTime.fromISO(flight.localDepartureDateTime!).toFormat('HH:mm'),
      [GtmDimensions.paxConfig]: paxConfig,
      [GtmDimensions.lowestFareSelected]: flight.lowestFare ? 'Yes' : 'No',
      [GtmDimensions.fareType]: flight.fares?.ADT?.STANDARD ? 'Standard' : flight.fares?.ADT?.FLEXI ? 'Flexi' : '',
      [GtmDimensions.urgencyMessageContent]: urgencyMessageContent,
      [GtmDimensions.urgencyMessageType]: 'Flight Level'
    };
  });

  return groupImpressionsByProperty(flights, 'category');
};
